/**
 * Filter drawer for API credentials (SUPER_ADMIN only). Always offers the Organizations
 * filter; the Developer Tools API Keys tab additionally opts into the Key Status filter
 * (`includeKeyStatus`) — the legacy view omits it until the backend search field ships.
 */
import { Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { GraphQL } from "@rpi/openapi-api";
import { useState } from "react";
import { FilterDrawer } from "../../components/filters/FilterDrawer";
import { MultiselectInput } from "../../components/Multiselect";
import {
  SearchInput,
  getInStatusFilter,
  getInitialFilterValues,
  toSearchInputArray,
} from "../../configuration/filters/searchInput";
import {
  getCustomerPublicIdFilter,
  getInitialOrganization,
  NO_FILTER_VALUE,
  OrganizationFilter,
} from "../../components/filters/OrganizationFilter";
import { useFilterStyles } from "../../components/filters/filterStyles";
import { apiKeyStatusRender } from "../../utils/searchField.util";

const filterFormId = "api-credentials-filter-form";

const allKeyStatuses = Object.values(GraphQL.ApiKeyStatus).map((status) => ({
  id: status,
  label: apiKeyStatusRender(status),
}));

type ApiCredentialsFilterProps = {
  filters: SearchInput<GraphQL.ApiCredentialSearchFields>[];
  includeKeyStatus?: boolean;
  onClose: () => void;
  onApply: (filter: SearchInput<GraphQL.ApiCredentialSearchFields>[]) => void;
};

export function ApiCredentialsFilter({
  isOpened,
  filters,
  includeKeyStatus,
  onClose,
  onApply,
}: { isOpened: boolean } & ApiCredentialsFilterProps) {
  return (
    <FilterDrawer formId={filterFormId} isOpened={isOpened} onClose={onClose}>
      <ApiCredentialsFilterContent
        filters={filters}
        includeKeyStatus={includeKeyStatus}
        onClose={onClose}
        onApply={onApply}
      />
    </FilterDrawer>
  );
}

function ApiCredentialsFilterContent({
  filters: initialFilters,
  includeKeyStatus,
  onClose,
  onApply,
}: ApiCredentialsFilterProps) {
  const { classes } = useFilterStyles();

  const { getInputProps, onSubmit, setFieldValue } = useForm({
    initialValues: {
      selectedCustomer: getInitialOrganization(GraphQL.ApiCredentialSearchFields.CustomerPublicId, initialFilters),
    },
  });
  const [keyStatusFilter, setKeyStatusFilter] = useState<GraphQL.ApiKeyStatus[]>(() =>
    getInitialFilterValues<GraphQL.ApiCredentialSearchFields, GraphQL.ApiKeyStatus>(
      GraphQL.ApiCredentialSearchFields.Status,
      initialFilters
    )
  );

  const handleApply = (customerPublicId: string) => {
    const filters = toSearchInputArray([
      getCustomerPublicIdFilter(GraphQL.ApiCredentialSearchFields.CustomerPublicId, customerPublicId),
      getInStatusFilter(GraphQL.ApiCredentialSearchFields.Status, keyStatusFilter),
    ]);
    onApply(filters);
    onClose();
  };

  const handleClear = () => {
    setFieldValue("selectedCustomer", NO_FILTER_VALUE);
    setKeyStatusFilter([]);
  };

  return (
    <Stack className={classes.root}>
      <form
        id={filterFormId}
        className={classes.form}
        onSubmit={onSubmit((values) => handleApply(values.selectedCustomer))}
        onReset={handleClear}
      >
        <OrganizationFilter {...getInputProps("selectedCustomer")} />
        {includeKeyStatus && (
          <MultiselectInput
            label="Key Status"
            items={allKeyStatuses}
            value={keyStatusFilter.map((status) => ({ id: status, label: apiKeyStatusRender(status) }))}
            onValueChange={(newValue) => {
              setKeyStatusFilter(newValue.map((value) => value.id));
            }}
            onDelete={(valueToDelete) => {
              setKeyStatusFilter((current) => current.filter((status) => status !== valueToDelete.id));
            }}
          />
        )}
      </form>
    </Stack>
  );
}
